import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const FraudCategoryOutputSchema_ = z.object({
  category: z.string(),
  confidence: z.number(),
  detected: z.boolean(),
  explanation: z.string(),
  signals: z.array(z.string()),
  summary: z.string(),
  evidence: z.array(z.string()),
});
export const FraudCategoryOutputSchema: z.ZodType<FraudCategoryOutputDashboard> =
  FraudCategoryOutputSchema_;
export interface FraudCategoryOutputDashboard
  extends z.infer<typeof FraudCategoryOutputSchema_> {}

const FraudAssessmentSchema_ = z.object({
  overallFraudScore: z.number(),
  categories: z.array(FraudCategoryOutputSchema),
  summary: z.string(),
  primaryCategory: z.string().nullable().optional(),
  riskDescription: z.string(),
});
export const FraudAssessmentSchema: z.ZodType<FraudAssessmentDashboard> =
  FraudAssessmentSchema_;
export interface FraudAssessmentDashboard
  extends z.infer<typeof FraudAssessmentSchema_> {}

const MerchantFraudFeedbackSchema_ = z.object({
  isFraud: z.boolean(),
  feedback: z.string().nullable().optional(),
});
export const MerchantFraudFeedbackSchema: z.ZodType<MerchantFraudFeedbackDashboard> =
  MerchantFraudFeedbackSchema_;
export interface MerchantFraudFeedbackDashboard
  extends z.infer<typeof MerchantFraudFeedbackSchema_> {}

const FraudReviewTeamSchema_ = z.object({ id: z.string(), name: z.string() });
export const FraudReviewTeamSchema: z.ZodType<FraudReviewTeam> =
  FraudReviewTeamSchema_;
export interface FraudReviewTeam
  extends z.infer<typeof FraudReviewTeamSchema_> {}

const FraudReviewReturnSchema_ = z.object({
  returnId: z.string(),
  teamId: z.string(),
  teamName: z.string(),
  status: z.string(),
  createdAt: z.string(),
  processedAt: z.string().nullable(),
  agentFraudReview: FraudAssessmentSchema.nullable(),
  merchantFraudFeedback: MerchantFraudFeedbackSchema.nullable(),
});
export const FraudReviewReturnSchema: z.ZodType<FraudReviewReturn> =
  FraudReviewReturnSchema_;
export interface FraudReviewReturn
  extends z.infer<typeof FraudReviewReturnSchema_> {}

const FraudReviewStatsSchema_ = z.object({
  totalReturns: z.number(),
  totalFraudReviewed: z.number(),
  totalFlaggedAsFraud: z.number(),
  totalWithMerchantFeedback: z.number(),
  agentAccuracy: z.number().nullable(),
});
export const FraudReviewStatsSchema: z.ZodType<FraudReviewStats> =
  FraudReviewStatsSchema_;
export interface FraudReviewStats
  extends z.infer<typeof FraudReviewStatsSchema_> {}

export const getFraudReviewTeams = unaryRpc({
  name: "getFraudReviewTeams" as const,
  input: z.object({}),
  output: z.object({ teams: z.array(FraudReviewTeamSchema) }),
});

export const getFraudReviewReturns = unaryRpc({
  name: "getFraudReviewReturns" as const,
  input: z.object({
    teamIds: z.array(z.string()).optional(),
    page: z.number(),
    pageSize: z.number(),
    startDate: z.string(),
    endDate: z.string(),
    dateField: z.enum(["createdAt", "processedAt"]),
    flaggedAsFraud: z.boolean().optional(),
  }),
  output: z.object({
    returns: z.array(FraudReviewReturnSchema),
    totalCount: z.number(),
  }),
});

export const getFraudReviewStats = unaryRpc({
  name: "getFraudReviewStats" as const,
  input: z.object({
    teamId: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    dateField: z.enum(["createdAt", "processedAt"]),
  }),
  output: FraudReviewStatsSchema,
});
